/**
 * Import ADIF Dialog
 * 
 * Dialog for importing logbook entries from ADIF/ADI file.
 */

import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, Upload, FileText, CheckCircle, XCircle } from "lucide-react";
import { logbookService } from "@/services/logbookService";
import { toast } from "sonner";

interface ImportADIFDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete?: () => void;
}

export function ImportADIFDialog({
  open,
  onOpenChange,
  onImportComplete,
}: ImportADIFDialogProps) {
  const [file, setFile] = React.useState<File | null>(null);
  const [importing, setImporting] = React.useState(false);
  const [importResult, setImportResult] = React.useState<{
    imported: number;
    errors: string[];
  } | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Check if file is .adi or .adif
      const extension = selectedFile.name.toLowerCase().split('.').pop();
      if (extension !== 'adi' && extension !== 'adif' && extension !== 'txt') {
        toast.error("Invalid file type", {
          description: "Please select a .adi, .adif, or .txt file",
        });
        return;
      }
      setFile(selectedFile);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error("No file selected", {
        description: "Please select an ADIF file to import",
      });
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      // Read file content
      const fileContent = await file.text();

      // Import ADIF
      const result = await logbookService.importADIF(fileContent);

      setImportResult(result);

      if (result.errors.length > 0) {
        toast.warning("Import completed with errors", {
          description: `${result.imported} entries imported, ${result.errors.length} errors`,
        });
      } else {
        toast.success("Import successful", {
          description: `${result.imported} entries imported successfully`,
        });
      }

      // Reset file and call onImportComplete
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      onImportComplete?.();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to import ADIF file";
      toast.error("Import failed", {
        description: errorMessage,
      });
      setImportResult(null);
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    if (!importing) {
      setFile(null);
      setImportResult(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Import ADIF File</DialogTitle>
          <DialogDescription>
            Upload an ADIF (.adi) or ADIF (.adif) file to import logbook entries.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="adif-file">ADIF File</Label>
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                id="adif-file"
                type="file"
                accept=".adi,.adif,.txt"
                onChange={handleFileSelect}
                disabled={importing}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                className="w-full"
              >
                <Upload className="mr-2 h-4 w-4" />
                {file ? file.name : "Select ADIF File"}
              </Button>
            </div>
            {file && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileText className="h-4 w-4" />
                <span>{file.name} ({(file.size / 1024).toFixed(2)} KB)</span>
              </div>
            )}
          </div>

          {importResult && (
            <div className="rounded-lg border p-4 space-y-2">
              <div className="flex items-center gap-2">
                {importResult.errors.length === 0 ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-yellow-600" />
                )}
                <span className="font-medium">
                  {importResult.imported} entries imported
                </span>
              </div>
              {importResult.errors.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-medium text-destructive mb-1">
                    {importResult.errors.length} errors:
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {importResult.errors.slice(0, 5).map((error, index) => (
                      <p key={index} className="text-xs text-muted-foreground">
                        {error}
                      </p>
                    ))}
                    {importResult.errors.length > 5 && (
                      <p className="text-xs text-muted-foreground">
                        ... and {importResult.errors.length - 5} more errors
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={importing}
          >
            {importResult ? "Close" : "Cancel"}
          </Button>
          {!importResult && (
            <Button
              type="button"
              onClick={handleImport}
              disabled={importing || !file}
            >
              {importing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Import
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

